import { useEffect, useState } from "react";
import { Plus, Trash2, Tag, Globe, MapPin } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const empty = {
  code: "", description: "", percent: "", min_subtotal: "",
  scope: "worldwide", once_per_account: false, stackable: false, free_shipping: false,
};

export const DiscountsTab = () => {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/admin/discounts")
      .then((r) => setCodes(r.data))
      .catch(() => setCodes([]))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const create = async (e) => {
    e.preventDefault();
    if (!form.code.trim()) { toast.error("Enter a code."); return; }
    const pct = parseFloat(form.percent) || 0;
    if (pct < 0 || pct > 100) { toast.error("Percent must be between 0 and 100."); return; }
    if (pct === 0 && !form.free_shipping) {
      toast.error("Add a % off, or enable Free shipping — a code needs at least one benefit."); return;
    }
    setSaving(true);
    try {
      await api.post("/admin/discounts", {
        code: form.code.trim(),
        description: form.description.trim(),
        percent: pct,
        min_subtotal: parseFloat(form.min_subtotal) || 0,
        scope: form.scope,
        once_per_account: form.once_per_account,
        stackable: form.stackable,
        free_shipping: form.free_shipping,
        active: true,
      });
      toast.success(`Code ${form.code.trim().toUpperCase()} created.`);
      setForm(empty);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create code.");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (c) => {
    try {
      await api.patch(`/admin/discounts/${c.id}`, { active: !c.active });
      load();
    } catch { toast.error("Could not update code."); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete code ${c.code}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/discounts/${c.id}`);
      toast.success(`Deleted ${c.code}.`);
      load();
    } catch { toast.error("Could not delete code."); }
  };

  const inputCls = "w-full border border-[#E5E5E5] rounded-md px-3 py-2.5 text-sm outline-none focus:ring-1 focus:ring-black";

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-10" data-testid="discounts-tab">
      {/* Existing codes */}
      <div>
        <h3 className="eyebrow mb-4">Active & Saved Codes</h3>
        {loading ? (
          <p className="text-sm text-[#737373]">Loading…</p>
        ) : codes.length === 0 ? (
          <p className="text-sm text-[#737373]">No discount codes yet. Create one on the right.</p>
        ) : (
          <div className="space-y-3">
            {codes.map((c) => (
              <div key={c.id} className={`border rounded-md p-4 ${c.active ? "border-[#E5E5E5]" : "border-dashed border-[#d8d1c4] opacity-60"}`} data-testid={`discount-row-${c.code}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <Tag strokeWidth={1.5} className="h-4 w-4 text-[#560216]" />
                      <span className="font-serif text-lg tracking-wide">{c.code}</span>
                      {Number(c.percent) > 0 && <span className="text-xs bg-[#f3ece1] px-2 py-0.5 rounded-full">{c.percent}% off</span>}
                      {c.free_shipping && <span className="text-xs bg-[#e6efe8] text-[#3a6b45] px-2 py-0.5 rounded-full">Free shipping</span>}
                    </div>
                    {c.description && <p className="text-sm text-[#5a5a5a] mt-1">{c.description}</p>}
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-[#737373] mt-2">
                      <span>Min ${Number(c.min_subtotal).toFixed(0)} CAD</span>
                      <span className="inline-flex items-center gap-1">
                        {c.scope === "canada" ? <><MapPin className="h-3 w-3" /> Canada only</> : <><Globe className="h-3 w-3" /> Worldwide</>}
                      </span>
                      {c.once_per_account && <span>· One use per account</span>}
                      {c.stackable && <span>· Stackable</span>}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2 shrink-0">
                    <button onClick={() => toggleActive(c)} className={`text-[10px] uppercase tracking-[0.15em] px-3 py-1 rounded-full ${c.active ? "bg-[#e6efe8] text-[#3a6b45]" : "bg-[#eee] text-[#737373]"}`} data-testid={`toggle-active-${c.code}`}>
                      {c.active ? "Active" : "Inactive"}
                    </button>
                    <button onClick={() => remove(c)} className="text-[#737373] hover:text-[#9a3b3b]" data-testid={`delete-discount-${c.code}`}>
                      <Trash2 strokeWidth={1.5} className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create form */}
      <div>
        <h3 className="eyebrow mb-4">Create a Code</h3>
        <form onSubmit={create} className="space-y-4 bg-[#faf8f5] border border-[#E5E5E5] rounded-md p-5">
          <div>
            <label className="eyebrow block mb-1">Code</label>
            <input value={form.code} onChange={(e) => set("code", e.target.value.toUpperCase())} placeholder="e.g. SUMMER10" className={`${inputCls} uppercase tracking-wide`} data-testid="new-discount-code" />
          </div>
          <div>
            <label className="eyebrow block mb-1">What it does <span className="text-[#a8a29a] normal-case">optional</span></label>
            <input value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="10% off orders over $50" className={inputCls} data-testid="new-discount-description" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="eyebrow block mb-1">% off subtotal <span className="text-[#a8a29a] normal-case">optional</span></label>
              <input type="number" min="0" max="100" value={form.percent} onChange={(e) => set("percent", e.target.value)} placeholder="0" className={inputCls} data-testid="new-discount-percent" />
            </div>
            <div>
              <label className="eyebrow block mb-1">Min order ($ CAD) <span className="text-[#a8a29a] normal-case">optional</span></label>
              <input type="number" min="0" value={form.min_subtotal} onChange={(e) => set("min_subtotal", e.target.value)} placeholder="0" className={inputCls} data-testid="new-discount-min" />
            </div>
          </div>
          <div>
            <label className="eyebrow block mb-1">Availability</label>
            <select value={form.scope} onChange={(e) => set("scope", e.target.value)} className={`${inputCls} bg-white`} data-testid="new-discount-scope">
              <option value="worldwide">Worldwide</option>
              <option value="canada">Canada only</option>
            </select>
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="new-discount-once">
            <input type="checkbox" checked={form.once_per_account} onChange={(e) => set("once_per_account", e.target.checked)} />
            One use per account (signed-in customers)
          </label>
          <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="new-discount-free-shipping">
            <input type="checkbox" checked={form.free_shipping} onChange={(e) => set("free_shipping", e.target.checked)} />
            Free shipping (waives shipping cost at checkout)
          </label>
          <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="new-discount-stackable">
            <input type="checkbox" checked={form.stackable} onChange={(e) => set("stackable", e.target.checked)} />
            Stackable with other codes
          </label>
          <p className="text-xs text-[#737373]">Percent comes off the subtotal. Enable <strong>Free shipping</strong> to also waive the shipping charge (set % to 0 for a shipping-only code). Amounts are in CAD.</p>
          <button type="submit" disabled={saving} className="btn-primary w-full inline-flex items-center justify-center gap-2 disabled:opacity-60" data-testid="create-discount-button">
            <Plus strokeWidth={1.5} className="h-4 w-4" /> {saving ? "Creating…" : "Create Code"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default DiscountsTab;
